import { useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import devLog from "@/lib/devLog";

/* Sticky mobile CTA.
   • Hidden while the user scrolls DOWN (reading) so the Tawk chat bubble
     stays unobstructed.
   • Slides up only when the user scrolls UP past 400px (intent to look
     around) OR on the initial appearance threshold.
   • Two non-intrusive dismiss paths: a small "X" pill on the right, OR
     swipe the bar downward. Both flip a sessionStorage flag so the bar
     stays hidden for the rest of the session. */
const DISMISS_KEY = "ofin.stickyCtaDismissed";

export default function StickyCTA() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  });
  const lastY = useRef(0);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch (err) {
      devLog.warn("stickyCTA: sessionStorage unavailable", err);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      if (dismissed) return;
      const y = window.scrollY;
      const dy = y - lastY.current;
      lastY.current = y;
      if (y < 400) {
        setShow(false);
        return;
      }
      // 6 px deadzone so micro jitters don't toggle the bar.
      if (dy < -6) setShow(true);
      else if (dy > 6) setShow(false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  return (
    <AnimatePresence>
      {show && !dismissed && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 22, stiffness: 220 }}
          className="sticky-mobile-cta lg:hidden"
          data-testid="sticky-cta"
          /* Swipe-down-to-dismiss. Drag is constrained so the bar can
             only travel a short distance, and elastic drag past 40 px
             commits the dismiss. */
          drag="y"
          dragConstraints={{ top: 0, bottom: 80 }}
          dragElastic={0.35}
          dragMomentum={false}
          onDragEnd={(_e, info) => {
            if (info.offset.y > 40 || info.velocity.y > 400) dismiss();
          }}
        >
          {/* Subtle drag-handle hint on top of the bar · communicates
              "swipe down to dismiss" without taking visual real-estate. */}
          <span
            aria-hidden
            className="absolute left-1/2 -translate-x-1/2 -top-1.5 h-1 w-9 rounded-full"
            style={{
              background:
                "color-mix(in srgb, var(--text-2) 50%, transparent)",
            }}
          />
          <a
            href="https://live.ofinancial.markets/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-solid-brand flex-1 justify-center text-sm py-3.5"
            data-testid="sticky-cta-primary"
          >
            {t("sticky.openAccount", "Open Account")} <ArrowRight size={14} />
          </a>
          <button
            onClick={dismiss}
            onPointerDown={(e) => e.stopPropagation()} /* don't trigger drag */
            aria-label={t("sticky.dismiss", "Dismiss")}
            data-testid="sticky-cta-dismiss"
            className="w-10 h-10 rounded-full bg-[color:var(--surface)] border border-[color:var(--line-strong)] text-[color:var(--text-2)] grid place-items-center shrink-0 active:scale-95 transition-transform"
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
